import { BlocksApiNews } from "@/src/models/news.model";
import { useTheme } from "@/src/styles/theme-provider";
import dayjs from "dayjs";
import { LinearGradient } from "expo-linear-gradient";
import { Save2 } from "iconsax-react-nativejs";
import { Image, Pressable, Text, TouchableOpacity, View } from "react-native";
import { formatUserName } from "@/src/utils/string.utils";

type Props = {
  item: BlocksApiNews;
  categoryMap?: Map<string, string>;
  onPress?: () => void;
  cardWidth?: number;
  cardHeight?: number;
  isSaved?: boolean;
  onSave?: () => void;
};

const NewsCard = ({
  item,
  categoryMap,
  onPress,
  cardWidth = 256,
  cardHeight = 160,
  isSaved = false,
  onSave,
}: Props) => {
  const { themeVars } = useTheme();
  const imageUrl = item.coverImageUrl ?? undefined;

  const resolvedCategories = (item.categoryIds ?? [])
    .map((id) => categoryMap?.get(id))
    .filter((name): name is string => !!name);
  const firstCategory = resolvedCategories[0];
  const extraCount = resolvedCategories.length - 1;
  const extraCountDisplayable = extraCount > 0 ? `+${extraCount}` : null;

  const authorName = item.authorSnapshot
    ? formatUserName(item.authorSnapshot.firstName, item.authorSnapshot.lastName)
    : item.CreatedBy;

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      style={{ width: cardWidth, marginRight: 12 }}
      onPress={onPress}
    >
      <View style={{ height: cardHeight }} className="rounded-2xl overflow-hidden">
        {imageUrl ? (
          <Image
            source={{ uri: imageUrl }}
            className="absolute w-full h-full"
            resizeMode="cover"
          />
        ) : (
          <View className="absolute w-full h-full bg-ash" />
        )}

        <LinearGradient
          colors={[
            themeVars["--color-shadow-darker"] as string,
            themeVars["--color-shadow-darker"] as string,
            themeVars["--color-shadow-darker"] as string,
            "transparent",
          ]}
          locations={[0, 0.22, 0.42, 0.75]}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />

        <View className="absolute top-3 left-3 right-3 flex-row justify-between">
          <View className="flex-row gap-1.5">
            {firstCategory && (
              <View className="rounded-full px-2 py-1.5 bg-primary">
                <Text className="text-xs font-semibold text-white">
                  {firstCategory}
                </Text>
              </View>
            )}

            {extraCountDisplayable && (
              <View className="bg-shadow-dark rounded-full px-2 py-1.5">
                <Text className="text-white text-xs font-semibold">
                  {extraCountDisplayable}
                </Text>
              </View>
            )}
          </View>

          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              onSave?.();
            }}
            hitSlop={8}
            className="bg-shadow-dark rounded-full w-7 h-7 items-center justify-center active:opacity-70 hover:bg-ash-3"
          >
            <Save2
              size={18}
              color="white"
              variant={isSaved ? "Bold" : "Linear"}
            />
          </Pressable>
        </View>

        <View className="absolute bottom-0 left-0 right-0 px-3 pt-5 pb-3.5">
          <Text
            numberOfLines={2}
            className="text-white text-[15px] font-semibold leading-snug"
          >
            {item.Title}
          </Text>

          <Text className="text-white/90 text-xs mt-1.5">
            <Text className="font-semibold text-white">{authorName}</Text>
            {" · "}
            {dayjs(item.CreatedDate).format("DD MMM YYYY")}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default NewsCard;
